import { createTheme } from '@mui/material/styles'
import mikeSharedTheme from '@mike/mike-shared-frontend/mike-shared-styles/mikeSharedTheme'
import MIKE_COLORS from '@mike/mike-shared-frontend/mike-shared-styles/mike-colors'

// The shared lib (already on MUI v5) augments the palette with
// `lightGrey`, `mediumGrey`, `darkGrey`, `ultimate`. Importing
// `mikeSharedTheme` from there pulls in that module augmentation
// transitively via its .d.ts.

const sharedComponents: any = mikeSharedTheme.components ?? {}

// `any` cast on the components block: under React 19 / @types/react 19,
// MUI's `OverridesStyleRules` derives stricter types that don't accept
// the JSS-style `&.Mui-*` selector objects we use. Functionality is
// unchanged at runtime — this is a typing-only widen.
export const theme = createTheme({
  ...mikeSharedTheme,
  components: {
    ...sharedComponents,
    // MUI v5's CssBaseline applies the `body1` variant (1rem / 16px) to
    // <body>, whereas v4 applied `body2` (0.875rem / 14px). That change
    // enlarged all inherited/unstyled text app-wide. Restore the v4 base
    // size so the app matches how it rendered before the upgrade.
    MuiCssBaseline: {
      ...(sharedComponents.MuiCssBaseline ?? {}),
      styleOverrides: {
        ...(sharedComponents.MuiCssBaseline?.styleOverrides ?? {}),
        body: {
          fontSize: '0.85rem',
          lineHeight: 1.0
        }
      }
    },
    MuiFormLabel: {
      ...(sharedComponents.MuiFormLabel ?? {}),
      styleOverrides: {
        ...(sharedComponents.MuiFormLabel?.styleOverrides ?? {}),
        root: {
          '&.Mui-focused': {
            color: MIKE_COLORS.DARKGREY_DEFAULT
          },
          '&.Mui-error': {
            color: MIKE_COLORS.PINK_DEFAULT
          }
        }
      }
    },
    MuiInputLabel: {
      ...(sharedComponents.MuiInputLabel ?? {}),
      styleOverrides: {
        ...(sharedComponents.MuiInputLabel?.styleOverrides ?? {}),
        filled: {
          color: MIKE_COLORS.DARKGREY_DEFAULT,
          fontSize: '1rem', // 16px
          lineHeight: 1.374, // 22px
          '&.Mui-focused:not(.Mui-error)': {
            color: MIKE_COLORS.DARKGREY_DEFAULT
          },
          '&.Mui-error': {
            color: MIKE_COLORS.PINK_DEFAULT
          },
          '&.Mui-disabled': {
            color: MIKE_COLORS.MEDIUMGREY_LIGHT
          }
        }
      }
    },
    MuiFilledInput: {
      ...(sharedComponents.MuiFilledInput ?? {}),
      styleOverrides: {
        ...(sharedComponents.MuiFilledInput?.styleOverrides ?? {}),
        root: {
          color: MIKE_COLORS.DARKGREY_DEFAULT,
          backgroundColor: MIKE_COLORS.MEDIUMGREY_LIGHT,
          '&:hover': {
            backgroundColor: MIKE_COLORS.MEDIUMGREY_DEFAULT
          },
          '&.Mui-error': {
            backgroundColor: MIKE_COLORS.MEDIUMGREY_DARK
          },
          '&.Mui-disabled': {
            backgroundColor: MIKE_COLORS.MEDIUMGREY_LIGHT
          },
          '&.Mui-focused': {
            color: MIKE_COLORS.BRANDBLUE_DEFAULT,
            backgroundColor: MIKE_COLORS.MEDIUMGREY_DARK
          }
        },
        underline: {
          color: MIKE_COLORS.BRANDBLUE_DEFAULT,
          '&:after:not(.Mui-error)': {
            borderBottom: '1px solid ' + MIKE_COLORS.DARKGREY_DEFAULT
          },
          '&:before:not(.Mui-error)': {
            borderBottom: '1px solid ' + MIKE_COLORS.DARKGREY_DEFAULT
          },
          '&.Mui-focused:before': {
            borderBottom: '2px solid ' + MIKE_COLORS.BRANDBLUE_DEFAULT
          },
          '&.Mui-focused:after': {
            borderBottom: '2px solid ' + MIKE_COLORS.BRANDBLUE_DEFAULT
          },
          '&.Mui-error:before': {
            borderBottom: '2px solid ' + MIKE_COLORS.PINK_DEFAULT
          },
          '&.Mui-error:after': {
            borderBottom: '2px solid ' + MIKE_COLORS.PINK_DEFAULT
          }
        },
        multiline: {
          color: MIKE_COLORS.BRANDBLUE_DEFAULT,
          '&:before:not(.Mui-error)': {
            borderBottom: '1px solid ' + MIKE_COLORS.DARKGREY_DEFAULT
          },
          '&:after:not(.Mui-error)': {
            borderBottom: '1px solid ' + MIKE_COLORS.DARKGREY_DEFAULT
          },
          '&.Mui-focused:before': {
            borderBottom: '2px solid ' + MIKE_COLORS.BRANDBLUE_DEFAULT
          },
          '&.Mui-focused:after': {
            borderBottom: '2px solid ' + MIKE_COLORS.BRANDBLUE_DEFAULT
          },
          '&.Mui-error:before': {
            borderBottom: '2px solid ' + MIKE_COLORS.PINK_DEFAULT
          },
          '&.Mui-error:after': {
            borderBottom: '2px solid ' + MIKE_COLORS.PINK_DEFAULT
          }
        }
      }
    },
    MuiFormHelperText: {
      ...(sharedComponents.MuiFormHelperText ?? {}),
      styleOverrides: {
        ...(sharedComponents.MuiFormHelperText?.styleOverrides ?? {}),
        root: {
          fontSize: '0.75rem',
          lineHeight: 1.66,
          color: MIKE_COLORS.DARKGREY_DEFAULT,
          backgroundColor: 'transparent',
          '&.Mui-error': {
            color: MIKE_COLORS.PINK_DEFAULT
          }
        }
      }
    },
    MuiSelect: {
      ...(sharedComponents.MuiSelect ?? {}),
      styleOverrides: {
        ...(sharedComponents.MuiSelect?.styleOverrides ?? {}),
        select: {
          backgroundColor: MIKE_COLORS.MEDIUMGREY_LIGHT
        }
      }
    },
    MuiMenuItem: {
      ...(sharedComponents.MuiMenuItem ?? {}),
      defaultProps: {
        ...(sharedComponents.MuiMenuItem?.defaultProps ?? {}),
        // dense was previously a prop on MuiMenuItem; in v5 it stays as a boolean
        dense: true
      },
      styleOverrides: {
        ...(sharedComponents.MuiMenuItem?.styleOverrides ?? {}),
        root: {
          color: MIKE_COLORS.BRANDBLUE_DEFAULT,
          backgroundColor: MIKE_COLORS.MEDIUMGREY_LIGHT,
          '&:hover': {
            backgroundColor: MIKE_COLORS.MEDIUMGREY_DEFAULT
          },
          '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus': {
            backgroundColor: MIKE_COLORS.MEDIUMGREY_DARK,
            fontWeight: 'bold'
          }
        }
      }
    },
    MuiInput: {
      ...(sharedComponents.MuiInput ?? {}),
      defaultProps: {
        ...(sharedComponents.MuiInput?.defaultProps ?? {}),
        margin: 'dense'
      }
    },
    MuiTextField: {
      ...(sharedComponents.MuiTextField ?? {}),
      defaultProps: {
        ...(sharedComponents.MuiTextField?.defaultProps ?? {}),
        variant: 'filled',
        margin: 'dense'
      }
    }
  } as any
})

export default theme
